import React, { useState, useEffect, useContext } from 'react';
import { createUseStyles } from 'react-jss';
import variables from '../../theme/variables';
import { ReactComponent as BackArrowIcon } from '../../assets/icons/BackArrowIcon.svg';
import { State } from '../../contexts/GlobalState';
import TransitionText from '../common/TransitionText';
import { useUHF } from '../../hooks/useUHF';
import { ReactComponent as LinkArrowIcon } from '../../assets/icons/LinkArrowIcon.svg';

const useStyles = createUseStyles(
    {
        wrap: {
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            minHeight: 'var(--app-height)',
            zIndex: 2,
        },

        inner: {
            width: '100%',
            minHeight: '100%',
            color: variables.palette.white,
            margin: '200px auto 0',
            maxWidth: 1440,
            padding: '0 14px',

            '@media (min-width: 1024px)': {
                padding: '0 60px',
            },
        },

        bg: {
            position: 'fixed',
            width: '300vw',
            height: '300vh',
            left: '-100vw',
            top: '-var(--app-height)',
            zIndex: -1,
            backgroundImage: `url(${window.rootUrl}/img/aboutBgLines.svg)`,
            backgroundPosition: 'center center',

            // transform: 'translateX(-200vw) rotate(20deg)', // translateX(0vw)
            background: variables.palette.black,
            transition: 'transform 0.6s ease-out',
        },

        closeBtnWrap: {
            position: 'absolute',
            height: 'calc(100% - 100px)',
            left: 14,
            top: 21 + variables.msftHeaderHeightMobile,

            '@media (min-width: 860px)': {
                top: 21 + variables.msftHeaderHeight,
            },

            '@media (min-width: 1024px)': {
                left: 60,
                top: 45 + variables.msftHeaderHeight,
            },
        },
        closeBtn: {
            position: 'sticky',
            left: 14,
            top: 21,

            '@media (min-width: 860px)': {
                top: 21 + variables.msftHeaderHeight,
            },

            '@media (min-width: 1024px)': {
                left: 60,
                top: 45,
            },

            background: 'rgba(255, 255, 255, 0.16)',
            border: '1px solid #CCCCCC',
            backdropFilter: 'blur(76px)',
            borderRadius: 8,
            color: variables.palette.white,
            fontSize: 18,
            fontWeight: 600,
            display: 'flex',
            alignItems: 'center',
            padding: '0 24px',
            height: 52,
            cursor: 'pointer',
            zIndex: 3000,

            '& svg': {
                marginRight: 8,
            },
        },

        title: {
            fontSize: 48,
            lineHeight: '52px',
            textAlign: 'center',
            marginBottom: 56,

            '@media (min-width: 1024px)': {
                fontSize: 52,
                lineHeight: '60px',
                marginBottom: 65,
                textAlign: 'left',
            },
        },

        content: {
            marginBottom: 64,

            '@media (min-width: 1024px)': {
                display: 'flex',
                marginBottom: 124,
            },
        },

        copy: {
            flexShrink: 1,
            fontSize: 18,
            lineHeight: '26px',

            '& p': {
                marginBottom: '1em',
            },

            '& a': {
                color: variables.palette.white,
            },

            '@media (min-width: 1024px)': {
                fontSize: 18,
                lineHeight: '28px',
            },
        },

        links: {
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '16px',

            '@media (min-width: 1024px)': {
                fontSize: 24,
                lineHeight: '36px',
                fontWeight: 400,
            },
        },

        image: {
            marginBottom: 40,

            '@media (min-width: 1024px)': {
                minWidth: 400,
                marginRight: 36,
                marginBottom: 0,
            },

            '@media (min-width: 1300px)': {
                minWidth: 517,
                marginRight: 116,
            },
        },

        subtitle: {
            fontSize: 18,
            lineHeight: '28px',
            marginTop: '2em',
            marginBottom: '1em',

            '@media (min-width: 1024px)': {
                fontSize: 32,
                lineHeight: '40px',
            },
        },

        outboundLink: {
            textDecoration: 'none',
            display: 'flex',
            alignItems: 'center',
        },
    },
    { name: 'About' }
);

export default function About({ onClose }) {
    const classes = useStyles();

    const { returnButtonTheme, setReturnButtonTheme } = useContext(State);
    const { footerHtml } = useUHF();

    const [state, setState] = useState('init'); // init | in | out
    const [contentState, setContentState] = useState('init'); // init | in | out

    useEffect(() => {
        setReturnButtonTheme('light');
        setTimeout(() => setState('in'), 1);
        setTimeout(() => setContentState('in'), 400);
    }, []);

    const handleClose = () => {
        setContentState('out');
        setTimeout(() => setState('out'), 600);
        setTimeout(onClose, 1400);
    };

    return (
        <div className={classes.wrap}>
            <div
                className={classes.bg}
                style={{
                    transform: `translateX(${
                        state === 'in' ? '0vw' : '-300vw'
                    }) rotate(20deg)`,
                }}
            ></div>
            <div className={classes.closeBtnWrap}>
                <TransitionText state={contentState}>
                    <button
                        className={classes.closeBtn}
                        onClick={handleClose}
                        style={{ ...returnButtonTheme }}
                    >
                        <BackArrowIcon />
                        &nbsp;Studio
                    </button>
                </TransitionText>
            </div>
            <div className={classes.inner}>
                <div className={classes.titleWrap}>
                    <h2 className={classes.title}>
                        <TransitionText duration={600} state={contentState}>
                            Sol LeWitt x Microsoft
                        </TransitionText>
                    </h2>
                </div>

                <div className={classes.content}>
                    <div className={classes.image}>
                        <TransitionText
                            duration={600}
                            state={contentState}
                            delay={contentState === 'in' ? 200 : 0}
                        >
                            <img
                                src={`${window.rootUrl}/img/aboutSol.webp`}
                                width="100%"
                                height="auto"
                            />
                        </TransitionText>
                    </div>
                    <div className={classes.copy}>
                        <TransitionText
                            duration={600}
                            state={contentState}
                            delay={contentState === 'in' ? 300 : 0}
                        >
                            <p>
                                The estate of Sol LeWitt, in partnership with
                                Microsoft, developed <em>Conceptualize This</em>{' '}
                                and the Sol LeWitt app to honor his
                                contributions to modern art.
                            </p>
                            <p>
                                Download the app to see his global installations
                                and map his career impacts. Locate and scan his
                                iconic wall drawings with AI to discover more
                                about his fascinating process. These
                                installations were drawn directly onto walls all
                                over the world.
                            </p>
                            <p>
                                <a
                                    href="https://unlocked.microsoft.com/sol-lewitt/"
                                    target="_blank"
                                    rel="noreferrer"
                                    className={classes.outboundLink}
                                >
                                    <span>Explore the app&nbsp;</span>
                                    <LinkArrowIcon />
                                </a>
                            </p>
                            <h3 className={classes.subtitle}>
                                About Sol LeWitt
                            </h3>
                            <p>
                                Sol LeWitt (1928–2007) spearheaded
                                Conceptualism, and was a leader of
                                Minimalism—art movements of the mid-‘60s
                                determined to reinvent art.
                            </p>
                            <p>
                                Sol defined Conceptualism as centering the idea
                                above all. Much like in architecture, detailed
                                planning of the piece preceded its creation.
                            </p>
                            <p>
                                Sol was prolific in creating thousands of
                                drawings, paintings, prints, installations, and
                                sculptures, or structures as he called them. He
                                is considered one of the most influential
                                artists of the 20th and 21st centuries. His
                                legacy continues to inspire creators around the
                                world.
                            </p>
                            <p>
                                Sol’s work can be found in the permanent
                                collections of hundreds of museums worldwide.
                                Major exhibitions have been held at Museum of
                                Modern Art, New York (1978); Stedelijk Museum,
                                Amsterdam (1984); San Francisco Museum of Modern
                                Art (2000); Museum of Contemporary Art Chicago
                                (1979, 2000); Whitney Museum of American Art
                                (2000); Dia:Beacon (2006); Centre Pompidou-Metz
                                (2011); and Fondazione Carriero (2017). Sol
                                LeWitt: A Wall Drawing Retrospective, which
                                features over 100 wall drawings, will remain on
                                view at MASS MoCA, North Adams, through 2043.
                            </p>
                        </TransitionText>
                    </div>
                </div>
            </div>

            <footer className={classes.footer}>
                <TransitionText
                    state={contentState}
                    duration={600}
                    delay={contentState === 'in' ? 600 : 0}
                >
                    <div dangerouslySetInnerHTML={{ __html: footerHtml }}></div>
                </TransitionText>
            </footer>
        </div>
    );
}
